import { useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { MdCheckCircle, MdPlayArrow } from 'react-icons/md';
import { Button } from '../../../Components/Button';
import { Spinner } from '../../../Components/Spinner';
import {
  AlexaAssessment,
  AlexaAssessmentStore,
  buildAssessment,
  getValidAssessment,
  isAssessmentStale
} from './alexa-assessments-utils';
import { assessAlexaReadiness } from './alexa-readiness-utils';
import { AuditedProduct } from './scoring-engine-constants';

const CONCURRENCY = 3;

type RunStatus = 'queued' | 'running' | 'done' | 'error';

interface RunRow {
  product: AuditedProduct;
  status: RunStatus;
  score?: number;
  error?: string;
}

interface AssessReadinessModalProps {
  products: AuditedProduct[];
  assessments: AlexaAssessmentStore;
  onSaveAssessment: (productId: string, assessment: AlexaAssessment | null) => void;
  onClose: () => void;
}

export const AssessReadinessModal = ({
  products,
  assessments,
  onSaveAssessment,
  onClose
}: AssessReadinessModalProps) => {
  // Candidates are fixed to the modal's opening snapshot so rows don't reshuffle mid-run.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const candidates = useMemo(() => products.filter(p => !getValidAssessment(assessments, p)), []);

  const [started, setStarted] = useState(false);
  const [rows, setRows] = useState<RunRow[]>(
    candidates.map(product => ({ product, status: 'queued' }))
  );
  const [selected, setSelected] = useState<Set<string>>(() => new Set(candidates.map(c => c.id)));
  const cancelledRef = useRef(false);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const runOne = async (index: number) => {
    if (cancelledRef.current) return;
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], status: 'running' };
      return next;
    });
    const row = rows[index];
    try {
      const res = await assessAlexaReadiness(row.product);
      if (cancelledRef.current) return;
      onSaveAssessment(row.product.id, buildAssessment(row.product, res));
      setRows(prev => {
        const next = [...prev];
        next[index] = { ...next[index], status: 'done', score: res.score };
        return next;
      });
    } catch (e) {
      if (cancelledRef.current) return;
      setRows(prev => {
        const next = [...prev];
        next[index] = { ...next[index], status: 'error', error: e.message };
        return next;
      });
    }
  };

  const startBatch = async () => {
    setStarted(true);
    const indices = rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => selected.has(r.product.id))
      .map(({ i }) => i);
    let cursor = 0;
    const worker = async () => {
      while (cursor < indices.length) {
        await runOne(indices[cursor++]);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, indices.length) }, worker));
  };

  const handleClose = () => {
    cancelledRef.current = true;
    onClose();
  };

  const selectedRows = rows.filter(r => selected.has(r.product.id));
  const completedCount = rows.filter(r => r.status === 'done').length;
  const errorCount = rows.filter(r => r.status === 'error').length;
  const totalToRun = selectedRows.length;
  const allDone = started && completedCount + errorCount >= totalToRun;

  const getFootnoteText = (): string => {
    if (allDone) return 'Done. The readiness donut and catalog now reflect the new scores.';
    if (started) return 'Scoring in parallel…';
    if (totalToRun > 0) return `${totalToRun} selected of ${candidates.length}.`;
    return 'Select at least one listing to assess.';
  };

  return (
    <Modal show centered size='lg' onHide={handleClose} className='scorecard-batch-modal'>
      <Modal.Header closeButton>
        <div>
          <span className='scorecard-modal-eyebrow'>Batch Alexa Readiness</span>
          <Modal.Title>
            {candidates.length} listing{candidates.length === 1 ? '' : 's'} not yet assessed
          </Modal.Title>
          <p className='scorecard-modal-subtitle'>
            Scores listings as-written (no rewrites). Uncheck any to skip — you only spend tokens on
            what&apos;s checked.
          </p>
        </div>
      </Modal.Header>
      <Modal.Body>
        {candidates.length === 0 ? (
          <div className='scorecard-modal-success-banner'>
            <MdCheckCircle />
            <p>Every listing already has a current readiness score.</p>
          </div>
        ) : (
          <div className='assess-readiness-rows'>
            {rows.map(row => {
              const isSelected = selected.has(row.product.id);
              const stale = isAssessmentStale(assessments, row.product);
              return (
                <div
                  key={row.product.id}
                  className={`assess-readiness-row${isSelected ? '' : ' is-deselected'}`}
                >
                  {!started ? (
                    <Checkbox checked={isSelected} onChange={() => toggle(row.product.id)} />
                  ) : (
                    <span className={`assess-readiness-status status-${row.status}`}>
                      {row.status === 'running' && <Spinner size={16} />}
                    </span>
                  )}
                  <div className='assess-readiness-row-info'>
                    <div>{row.product.title === ' — ' ? '(no title)' : row.product.title}</div>
                    <div className='assess-readiness-row-meta'>
                      {row.product.asin} ·{' '}
                      {row.product.brand === ' — ' ? 'Independent' : row.product.brand}
                      {stale && <span> · copy changed</span>}
                    </div>
                    {row.error && <div className='assess-readiness-row-error'>{row.error}</div>}
                  </div>
                  {row.status === 'done' && row.score !== undefined && (
                    <span className='assess-readiness-row-score'>{row.score}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <p className='scorecard-modal-footnote'>{getFootnoteText()}</p>
        {!started && candidates.length > 0 && (
          <Button leadingElement={<MdPlayArrow />} disabled={totalToRun === 0} onClick={startBatch}>
            Assess {totalToRun} Listing{totalToRun === 1 ? '' : 's'}
          </Button>
        )}
        {(allDone || candidates.length === 0) && <Button onClick={handleClose}>Close</Button>}
      </Modal.Footer>
    </Modal>
  );
};
